<template>
    <component :is="to ? 'router-link' : 'button'" :to="to" :type="type" class="kv-button" :class="[variantClass, sizeClass, alignmentClass, { disabled, 'kv-active': active, 'kv-full-width': fullWidth, 'kv-loading': loading }]" @click="handleClick" @dblclick="handleDoubleClick" :disabled="disabled && !to">
        <div class="kv-button-content">
            <Icon v-if="prependIcon" :icon-class="prependIcon" :size="calculatedIconSize" />
            <slot></slot>
            <Icon v-if="appendIcon" :icon-class="appendIcon" :size="calculatedIconSize" />
        </div>

        <span v-if="loading" class="kv-loader"></span>
    </component>
</template>

<script>
import Icon from "../icon/icon.vue";

export default {
    name: "KvButton",
    components: { Icon },
    props: {
        variant: {
            type: String,
            default: "filled", // 'filled' | 'outlined' | 'text' | 'tonal'
        },
        size: {
            type: String,
            default: "regular", // 'regular' | 'small' | 'large'
        },
        alignment: {
            type: String,
            default: "center", // 'left' | 'center' | 'right'
            validator: (value) => ["left", "center", "right"].includes(value),
        },
        prependIcon: {
            type: String,
            default: null,
        },
        appendIcon: {
            type: String,
            default: null,
        },
        iconSize: [String, Number],
        type: {
            type: String,
            default: "button",
        },
        to: {
            type: [String, Object],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        active: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        variantClass() {
            return `kv-${this.variant}-button`;
        },
        sizeClass() {
            return `kv-${this.size}-button`;
        },
        alignmentClass() {
            return `kv-alignment-${this.alignment}`;
        },
        calculatedIconSize() {
            // Se o dev passou manualmente, respeita o valor
            if (this.iconSize) return this.iconSize;

            // Caso contrário, define automático
            switch (this.size) {
                case "small":
                    return 1;
                case "large":
                    return 4;
                case "extra-large":
                    return 5;
                default:
                    return 3;
            }
        },
    },
    methods: {
        handleClick(e) {
            if (this.disabled || this.loading) {
                e.preventDefault();
                return;
            }
            this.$emit("click", e);
        },
        handleDoubleClick(e) {
            if (this.disabled || this.loading) {
                e.preventDefault();
                return;
            }
            this.$emit("dblclick", e);
        },
    },
};
</script>

<style lang="less">
.kv-button {
    border-radius: 8px;
    padding: 8px 9px;
    cursor: pointer;
    transition: all ease-in-out 0.3s;
    outline: none;
    border: 1px solid transparent;
    font-size: 13px;
    height: 36px;
    position: relative;
    display: inline-flex;
    text-decoration: none !important;
    white-space: nowrap;
    min-width: 36px;
    font-size: 14px;

    .kv-button-content {
        display: inline-flex;
        line-height: 1;
        align-items: center;
        gap: 8px;
        height: 100%;
    }

    /* FULL WIDTH */
    &.kv-full-width {
        width: 100%;
    }

    /* Alinhamentos */
    &.kv-alignment-left {
        justify-content: flex-start;
        text-align: left;
    }

    &.kv-alignment-center {
        justify-content: center;
        text-align: center;
    }

    &.kv-alignment-right {
        justify-content: flex-end;
        text-align: right;
    }

    &.kv-small-button {
        font-size: 13px;
        padding: 6px 9px;
        height: 30px;
    }

    &.kv-large-button {
        font-size: 16px;
        height: 42px;
        min-width: 42px;
    }

    &.kv-extra-large-button {
        font-size: 17px;
        height: 45px;
        min-width: 45px;
    }

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
    }


    /* TEXT BUTTON */
    &.kv-text-button {
        background: transparent;
        color: #1f2a37;

        .ui-icon {
            color: #1f2a37;
        }

        &:hover,
        &.kv-active {
            color: #2aaa4c;
            background-color: #f1fcf3;

            .ui-icon {
                color: #2aaa4c;
            }
        }
    }

    /* TONAL & OUTLINED BUTTON */
    &.kv-tonal-button,
    &.kv-outlined-button {
        background-color: #fcfcfd;
        color: #1f2a37;
        font-weight: 500;

        .ui-icon-wrapper {
            display: flex;
        }

        &:hover,
        &.kv-active {
            background-color: #e5e7eb;
        }
    }

    /* OUTLINED BUTTON */
    &.kv-outlined-button {
        border-color: #e5e7eb;
    }

    /* FILLED BUTTON */
    &.kv-filled-button {
        background-color: #2aaa4c;
        color: #f1fcf3;

        &:hover {
            background-color: #00542e;
        }
    }

    /* TINTED BUTTON */
    &.kv-tinted-button {
        background-color: #F3FAF4;
        color: #1D7836;
        border-color: #2AAA4C;
    }

    /* AI BUTTON */
    &.kv-ai-button {
        color: #F1FCF3;
        border: none;
        background: linear-gradient(135deg,
                #F49E8A 0%,
                #E83E76 30%,
                #936CAD 65%,
                #2DBBDD 100%);
    }

    &.kv-loading .kv-button-content {
        opacity: 0;
    }

    .kv-loader {
        position: absolute;
        height: calc(100% - 12px);
        aspect-ratio: 1 / 1;
        border: 2px solid transparent;
        border-bottom-color: #FFF;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
    }

    &.kv-outlined-button .kv-loader,
    &.kv-text-button .kv-loader {
        // border-color: #e5e7eb;
        border-bottom-color: #2aaa4c;
    }

    @keyframes rotation {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
